import React from 'react';
import { Button, Grid } from '@material-ui/core';
import { Helmet } from 'react-helmet';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Alert, Box, LinearProgress } from '@mui/material';

import { selectOrganization, selectUser } from 'entities/user/selectors';
import { useCommunityFullQuery } from 'generated/graphql';
import { ViewInAppButton } from 'components/ViewInAppButton';
import { getAppDomain } from 'utils/getAppDomain';
import Img from 'retired/Img';
import BackButtonLayoutTitleAction from 'retired/BackButtonLayout/BackButtonLayoutTitleAction';
import TickSquare from 'assets/icons/tick-square.svg';
import CloseSquare from 'assets/icons/close-square.svg';
import { red, statusTextSuccess } from 'utils/color-styles';
import { getEntityPathForApp } from 'components/TeamsCommunities/EntityTable';

import { useRedirectIfNoCommunityAccess } from '../CommunitiesPage/hasCommunityAccess';

import { Banner } from './Banner';
import { DeedsList } from './DeedsList';
import { MembersList } from './MembersList';
import { CommunityInfo } from './CommunityInfo/CommunityInfo';

// @NOTE-DP: extract to MUI theme once we have proper styleguide in place
export const Header = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <h3 style={{ fontWeight: 500, fontSize: 16 }}>{children}</h3>
);

const CommunitiesPage = (): JSX.Element | null => {
  const user = useSelector(selectUser);
  const organization = useSelector(selectOrganization);
  const hasAccess = useRedirectIfNoCommunityAccess(user, organization);
  const { id: communityId } = useParams<{ id: string }>();
  const { data, error, loading } = useCommunityFullQuery({ variables: { communityId } });

  const community = data?.community;
  const location = useLocation();
  const isTeam = community?.type === 'Team' || location.pathname.startsWith('/teams');
  const entityTypePlural = isTeam ? 'teams' : 'communities';

  if (error) {
    return <Alert severity="error">An error occurred when fetching the community</Alert>;
  }

  if ((!community || !hasAccess) && !loading) {
    return null;
  }

  if (loading) {
    return (
      <Box>
        <LinearProgress />
      </Box>
    );
  }

  const viewInAppButton = (
    <div
      style={{
        display: 'inline-flex',
        marginLeft: 'auto',
        fontSize: 15,
        padding: '6px 20px',
        textDecorationLine: 'underline',
        textUnderlinePosition: 'under',
      }}
    >
      <ViewInAppButton
        href={`${getAppDomain([organization, user])}${getEntityPathForApp(
          entityTypePlural
        )}/${communityId}`}
        text="View in App"
      />
    </div>
  );
  const editButton = (
    <div>
      <Button
        color="primary"
        variant="contained"
        component={Link}
        to={`/${entityTypePlural}/edit/${communityId}`}
      >
        Edit
      </Button>
    </div>
  );

  const shouldDisplayCommunityInfo = !(
    community.Nonprofits?.length === 0 && community.leads?.length === 0
  );

  return (
    <>
      <Helmet title="Communities" />
      <BackButtonLayoutTitleAction
        title={community?.title}
        to={`/${entityTypePlural}`}
        rightActions={
          <>
            {viewInAppButton}
            {editButton}
          </>
        }
        subtitle={
          community?.enabled === null || community?.enabled ? (
            <span style={{ fontSize: 14, color: statusTextSuccess }}>
              Active&nbsp;
              <img src={TickSquare} alt="active" style={{ width: 16 }} />
            </span>
          ) : (
            <span style={{ fontSize: 14, color: red }}>
              Inactive&nbsp;
              <img src={CloseSquare} alt="active" style={{ width: 16 }} />
            </span>
          )
        }
      >
        <Grid container spacing={4}>
          <Grid
            item
            style={{ height: 366 }}
            {...(shouldDisplayCommunityInfo ? { xs: 6, md: 7, lg: 8 } : { xs: 12 })}
          >
            <Banner community={community} />
          </Grid>
          {shouldDisplayCommunityInfo && (
            <Grid item xs={6} md={5} lg={4} style={{ minHeight: 366 }}>
              <CommunityInfo community={community} />
            </Grid>
          )}
        </Grid>

        <Grid container spacing={4} style={{ paddingTop: 26 }}>
          {community?.Deeds?.length ? (
            <Grid item xs={12} lg={6}>
              <DeedsList deeds={community?.Deeds} />
            </Grid>
          ) : null}
          <Grid item xs={12} lg={6}>
            <MembersList communityId={communityId} communityType={community?.type} />
          </Grid>
        </Grid>
      </BackButtonLayoutTitleAction>
    </>
  );
};

export default CommunitiesPage;
